import React, { useState, useEffect, useCallback, useMemo } from "react";
import { formatDateTime } from "../../utils/textUtils";
import {
  CompareMessage,
  RootNode,
  BenchmarkResults,
  BenchmarkCompareResponse,
  BenchmarkActionResponse,
} from "../../types/debuggerServiceTypes";
import MessageItem from "../MessageItem";
import { ChatMessage, Option } from "../../types/chat";
import { debuggerService } from "../../services/DebuggerService";
import { makeResolveVirtualRequest } from "../../services/ChatService";
import { useAuth } from "../../providers/AuthContext";

interface CompareMessagesTableProps {
  messages: CompareMessage[];
  onUpdateMessage?: (queryId: string, updates: Partial<CompareMessage>) => void;
  hideReviewed: boolean;
  onToggleHideReviewed: () => void;
  showReviewedOnly: boolean;
  onToggleShowReviewedOnly: () => void;
  benchmarkResults?: BenchmarkResults | null;
  onReviewedCountChange?: (count: number) => void;
  onBenchmarkResultsUpdate?: (results: BenchmarkResults) => void;
}

type SortField =
  | "bot_message_reply_uuid"
  | "expected_response"
  | "pruned_expected_response"
  | null;
type SortDirection = "asc" | "desc";

const CompareMessagesTable: React.FC<CompareMessagesTableProps> = ({
  messages,
  onUpdateMessage,
  hideReviewed,
  onToggleHideReviewed,
  showReviewedOnly,
  onToggleShowReviewedOnly,
  benchmarkResults,
  onReviewedCountChange,
  onBenchmarkResultsUpdate,
}) => {
  const { getUserEmail } = useAuth();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rootNodes, setRootNodes] = useState<RootNode[]>([]);
  const [loadingRootNodes, setLoadingRootNodes] = useState(false);
  const [treeNodesMap, setTreeNodesMap] = useState<Map<string, RootNode[]>>(
    new Map()
  );
  const [loadingTreeNodes, setLoadingTreeNodes] = useState(false);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterValue, setFilterValue] = useState<string>("");
  const [loadedUserReplyingMessages, setLoadedUserReplyingMessages] = useState<
    Map<string, ChatMessage>
  >(new Map());
  const [loadingUserReplyingMessages, setLoadingUserReplyingMessages] =
    useState<Set<string>>(new Set());
  const [userFeedbackValues, setUserFeedbackValues] = useState<
    Map<string, string>
  >(new Map());
  const [reloadingRows, setReloadingRows] = useState<Set<string>>(new Set());

  // Fetch root nodes on component mount
  useEffect(() => {
    const fetchRootNodes = async () => {
      setLoadingRootNodes(true);
      try {
        const response = await debuggerService.getRootNodes();
        setRootNodes(response.root_nodes);
      } catch (error) {
        console.error("Error fetching root nodes:", error);
      } finally {
        setLoadingRootNodes(false);
      }
    };

    fetchRootNodes();
  }, []);

  // Calculate and notify about reviewed count
  useEffect(() => {
    const reviewedCount = messages.filter(
      (message) => message.has_been_reviewed === "true"
    ).length;
    onReviewedCountChange?.(reviewedCount);
  }, [messages, onReviewedCountChange]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort and filter messages
  const processedMessages = useMemo(() => {
    let filteredMessages = messages;

    // Apply reviewed status filtering
    if (hideReviewed) {
      filteredMessages = filteredMessages.filter(
        (message) => message.has_been_reviewed !== "true"
      );
    }

    if (showReviewedOnly) {
      filteredMessages = filteredMessages.filter(
        (message) => message.has_been_reviewed === "true"
      );
    }

    // Apply text filter
    if (filterValue.trim()) {
      const filterLower = filterValue.toLowerCase();
      filteredMessages = filteredMessages.filter((message) => {
        // only include messages that match the filter
        return (
          message.bot_message_reply_uuid?.toLowerCase().includes(filterLower) ||
          message.expected_response?.toLowerCase().includes(filterLower) ||
          message.pruned_expected_response
            ?.toLowerCase()
            .includes(filterLower) ||
          message.user_message.toLowerCase().includes(filterLower) ||
          message.bot_message_reply_text?.toLowerCase().includes(filterLower) ||
          message.query_id.toLowerCase().includes(filterLower) ||
          message.session_id.toLowerCase().includes(filterLower) ||
          message.user_email.toLowerCase().includes(filterLower)
        );
      });
    }

    // Apply sorting
    if (sortField) {
      filteredMessages = [...filteredMessages].sort((a, b) => {
        const aValue = a[sortField] || ""; // gets value for sorting or "" if not found
        const bValue = b[sortField] || "";

        const comparison = aValue.localeCompare(bValue); //returns -1 if a < b, 0 if a == b, 1 if a > b
        return sortDirection === "asc" ? comparison : -comparison; // flips value if we need to sort descending
      });
    }

    return filteredMessages;
  }, [
    messages,
    sortField,
    sortDirection,
    filterValue,
    hideReviewed,
    showReviewedOnly,
  ]);

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path // up and down arrow icon
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return (
      <svg
        className={`w-4 h-4 ${sortDirection === "asc" ? "text-blue-600" : "text-blue-600"}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={sortDirection === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} //ascending is up, descending is down arrow
        />
      </svg>
    );
  };

  // Convert CompareMessage to ChatMessage for MessageItem
  const convertToChatMessage = (
    message: CompareMessage,
    type: "user" | "bot"
  ): ChatMessage => {
    if (type === "user") {
      return {
        id: `${message.query_id}-user`,
        messageId: message.query_id,
        text: message.user_message,
        dateTime: new Date(message.user_message_sent_datetime),
        messageType: "user",
        nodeUuid: message.user_replying_to_node_uuid,
        options: [],
        audio: [],
        media: "",
      };
    } else {
      return {
        id: `${message.query_id}-bot`,
        messageId: message.query_id,
        text: message.bot_message_reply_text || "",
        dateTime: new Date(
          message.bot_message_reply_datetime ||
            message.user_message_sent_datetime
        ),
        messageType: "bot",
        nodeUuid: message.bot_message_reply_uuid,
        options: message.bot_message_reply_options || [],
        audio: message.bot_message_reply_audio
          ? Array.isArray(message.bot_message_reply_audio)
            ? message.bot_message_reply_audio
            : [message.bot_message_reply_audio]
          : [],
        media: message.bot_message_reply_media || "",
        origin: message.bot_message_reply_origin,
      };
    }
  };

  // Convert new message response to ChatMessage for display
  const convertBenchmarkCompareResponseToChatMessage = (
    newResponse: BenchmarkCompareResponse | BenchmarkActionResponse,
    queryId: string
  ): ChatMessage | null => {
    // Check if it's a BenchmarkCompareResponse
    if ("new_reply_node_uuid" in newResponse && "question" in newResponse) {
      return {
        id: `${queryId}-new-bot`,
        messageId: queryId,
        text: newResponse.text || "",
        dateTime: new Date(),
        messageType: "bot",
        nodeUuid: newResponse.new_reply_node_uuid,
        options: newResponse.options || [],
        audio: newResponse.audio
          ? Array.isArray(newResponse.audio)
            ? newResponse.audio
            : [newResponse.audio]
          : [],
        media: newResponse.media || "",
        origin: newResponse.question,
      };
    }

    // If it's a BenchmarkActionResponse, return null since we can't display it as a ChatMessage
    return null;
  };

  const toggleRowExpansion = (queryId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(queryId)) {
      newExpandedRows.delete(queryId);
    } else {
      newExpandedRows.add(queryId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleFoundCorrectNodeChange = (queryId: string, value: string) => {
    onUpdateMessage?.(queryId, { found_correct_node: value });
  };

  const handleHasBeenReviewedChange = (queryId: string, value: string) => {
    onUpdateMessage?.(queryId, { has_been_reviewed: value });
  };

  const handleIsFlaggedChange = (queryId: string, value: string) => {
    onUpdateMessage?.(queryId, { is_flagged: value });
  };

  const handleNeedsNewTreeChange = (queryId: string, value: string) => {
    onUpdateMessage?.(queryId, { needs_a_new_tree: value });
  };

  const handleUserFeedbackChange = (queryId: string, value: string) => {
    // Update local state immediately for responsive UI
    setUserFeedbackValues((prev) => new Map(prev).set(queryId, value));
  };

  const handleUserFeedbackBlur = (queryId: string) => {
    // Only update backend if there's actually a value in userFeedbackValues
    if (userFeedbackValues.has(queryId)) {
      const newValue = userFeedbackValues.get(queryId) || "";
      // Replace the existing feedback with the new value instead of appending
      onUpdateMessage?.(queryId, { user_feedback: newValue });
    }
  };

  // Alternative: Get value with lazy initialization
  const getFeedbackValue = useCallback(
    (queryId: string, originalValue: string | null | undefined) => {
      if (userFeedbackValues.has(queryId)) {
        return userFeedbackValues.get(queryId) || "";
      }
      return originalValue || "";
    },
    [userFeedbackValues]
  );

  // Get display text for a UUID from tree nodes
  const getNodeDisplayText = useCallback(
    (uuid: string, treeUuid: string): string => {
      if (!treeUuid || !treeNodesMap.has(treeUuid)) {
        return uuid;
      }

      const treeNodes = treeNodesMap.get(treeUuid);
      const node = treeNodes?.find((n) => n.uuid === uuid);
      return node ? node.content : uuid;
    },
    [treeNodesMap]
  );

  const handleExpectedResponseChange = async (
    queryId: string,
    value: string
  ) => {
    const currentMessage = messages.find((m) => m.query_id === queryId);

    if (currentMessage && currentMessage.expected_response !== value) {
      // Check if the current pruned_expected_response exists in the new tree
      let shouldClearPruned = true;

      if (currentMessage.pruned_expected_response && value) {
        try {
          // Fetch the new tree nodes to check if the pruned response exists
          const response = await debuggerService.getTreeNodes(value);
          const newTreeNodes = response.root_nodes;

          // Check if the current pruned response exists in the new tree
          const prunedNodeExists = newTreeNodes.some(
            (node) => node.uuid === currentMessage.pruned_expected_response
          );

          if (prunedNodeExists) {
            shouldClearPruned = false;
          }

          // Update the tree nodes map
          setTreeNodesMap((prev) => new Map(prev).set(value, newTreeNodes));
        } catch (error) {
          console.error("Error checking pruned response in new tree:", error);
        }
      }

      onUpdateMessage?.(queryId, {
        expected_response: value,
        pruned_expected_response: shouldClearPruned
          ? undefined
          : currentMessage.pruned_expected_response,
      });
    } else {
      onUpdateMessage?.(queryId, { expected_response: value });
    }

    // If a tree is selected and we don't have its nodes yet, fetch them
    if (value && !treeNodesMap.has(value)) {
      setLoadingTreeNodes(true);
      try {
        const response = await debuggerService.getTreeNodes(value);
        setTreeNodesMap((prev) =>
          new Map(prev).set(value, response.root_nodes)
        );
      } catch (error) {
        console.error("Error fetching tree nodes:", error);
      } finally {
        setLoadingTreeNodes(false);
      }
    }
  };

  const handlePrunedExpectedResponseChange = (
    queryId: string,
    value: string
  ) => {
    onUpdateMessage?.(queryId, { pruned_expected_response: value });
  };

  const handleReloadRow = async (message: CompareMessage) => {
    const queryId = message.query_id;

    if (reloadingRows.has(queryId)) {
      return; // Already reloading
    }

    setReloadingRows((prev) => new Set(prev).add(queryId));

    try {
      // 1. Refetch the message from DynamoDB
      const refreshedMessage = await debuggerService.getCompareMessage(queryId);
      if (!refreshedMessage) {
        throw new Error("Failed to fetch refreshed message");
      }

      // 2. Refetch root nodes to get any new local trees
      const rootNodesResponse = await debuggerService.getRootNodes();
      setRootNodes(rootNodesResponse.root_nodes);

      // 3. Run single benchmark compare with benchmark prefix
      const benchmarkResult = await debuggerService.runSingleBenchmarkCompare(
        refreshedMessage,
        "benchmark"
      );

      // 4. Update benchmark results
      if (
        "new_reply_node_uuid" in benchmarkResult ||
        (typeof benchmarkResult === "object" &&
          benchmarkResult !== null &&
          "action" in benchmarkResult)
      ) {
        const newBenchmarkResults = {
          ...benchmarkResults,
          new_message_responses: {
            ...(benchmarkResults?.new_message_responses || {}),
            [queryId]: benchmarkResult,
          },
        };
        onBenchmarkResultsUpdate?.(newBenchmarkResults as BenchmarkResults);
      }
    } catch (error) {
      console.error("Error reloading row:", error);
    } finally {
      setReloadingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(queryId);
        return newSet;
      });
    }
  };

  const handleLoadUserReplyingMessage = async (message: CompareMessage) => {
    if (!message.user_replying_to_node_uuid) {
      return;
    }

    const queryId = message.query_id;
    if (
      loadedUserReplyingMessages.has(queryId) ||
      loadingUserReplyingMessages.has(queryId)
    ) {
      return;
    }

    setLoadingUserReplyingMessages((prev) => new Set(prev).add(queryId));

    try {
      const chatMessage = await makeResolveVirtualRequest({
        nodeUUID: message.user_replying_to_node_uuid,
        textClicked: message.user_replying_to_node_uuid,
        queryId: "00000000-0000-0000-0000-000000000000",
        isOptionClick: false,
        userEmail: getUserEmail(),
        logRequest: false,
        prefix: "benchmark",
      });

      setLoadedUserReplyingMessages((prev) =>
        new Map(prev).set(queryId, chatMessage)
      );
    } catch (error) {
      console.error("Error loading user replying message:", error);
    } finally {
      setLoadingUserReplyingMessages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(queryId);
        return newSet;
      });
    }
  };

  return (
    <div className="flex-1 min-h-0">
      <div className="bg-white rounded-lg shadow h-full flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                Compare Messages Review
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Mark incorrect matches with their correct response for tuning
                the search model.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={hideReviewed}
                  onChange={onToggleHideReviewed}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Hide Reviewed
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showReviewedOnly}
                  onChange={onToggleShowReviewedOnly}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Show Reviewed Only
                </span>
              </label>
            </div>
          </div>

          {/* Filter Input */}
          <div className="mt-4">
            <div className="flex items-center space-x-2">
              <label
                htmlFor="filter-input"
                className="text-sm font-medium text-gray-700"
              >
                Filter:
              </label>
              <input
                id="filter-input"
                type="text"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="Paste or type to filter by UUID, text, email, etc..."
                className="flex-1 max-w-md rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              {filterValue && (
                <button
                  onClick={() => setFilterValue("")}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear filter"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            {(filterValue || hideReviewed || showReviewedOnly) && (
              <p className="mt-1 text-xs text-gray-500">
                Showing {processedMessages.length} of {messages.length} messages
                {filterValue && " (filtered)"}
                {hideReviewed && " (hiding reviewed)"}
                {showReviewedOnly && " (showing reviewed only)"}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden min-h-0">
          <div className="overflow-x-auto overflow-y-auto h-full">
            <table className="w-[94vw] max-w-[94vw] divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[6%]">
                    Reviewed
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                    Found Correct Node
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    User Replying To Node
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                    User Message
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    Endpoint Result
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                    <button
                      onClick={() => handleSort("bot_message_reply_uuid")}
                      className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                    >
                      <span>Bot Reply</span>
                      <SortIndicator field="bot_message_reply_uuid" />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    <button
                      onClick={() => handleSort("expected_response")}
                      className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                    >
                      <span>Expected Response</span>
                      <SortIndicator field="expected_response" />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    <button
                      onClick={() => handleSort("pruned_expected_response")}
                      className="flex items-center space-x-1 hover:text-gray-700 transition-colors"
                    >
                      <span>Pruned Expected Response</span>
                      <SortIndicator field="pruned_expected_response" />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                    Distance
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    Needs a New Tree
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[6%]">
                    Flagged
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">
                    User Feedback
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {processedMessages.map((message) => (
                  <React.Fragment key={message.query_id}>
                    <tr className="hover:bg-gray-50">
                      {/* Reviewed checkbox */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={message.has_been_reviewed === "true"}
                          onChange={(e) =>
                            handleHasBeenReviewedChange(
                              message.query_id,
                              e.target.checked ? "true" : "false"
                            )
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      {/* Found Correct Node */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <select
                          value={message.found_correct_node}
                          onChange={(e) =>
                            handleFoundCorrectNodeChange(
                              message.query_id,
                              e.target.value
                            )
                          }
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="unknown">Unknown</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </td>
                      {/* User Replying To Node */}
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="max-w-full">
                          {message.user_replying_to_node_uuid ? (
                            <>
                              {!loadedUserReplyingMessages.has(
                                message.query_id
                              ) &&
                                !loadingUserReplyingMessages.has(
                                  message.query_id
                                ) && (
                                  <div>
                                    <div className="text-xs text-gray-500 mb-2">
                                      Node UUID:{" "}
                                      {message.user_replying_to_node_uuid}
                                    </div>
                                    <button
                                      onClick={() =>
                                        handleLoadUserReplyingMessage(message)
                                      }
                                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                    >
                                      Load Message
                                    </button>
                                  </div>
                                )}
                              {loadingUserReplyingMessages.has(
                                message.query_id
                              ) && (
                                <div className="text-xs text-gray-500">
                                  Loading...
                                </div>
                              )}
                              {loadedUserReplyingMessages.has(
                                message.query_id
                              ) && (
                                <MessageItem
                                  {...loadedUserReplyingMessages.get(
                                    message.query_id
                                  )!}
                                  isReadOnly={true}
                                  loadMedia={false}
                                  hideLogo={true}
                                />
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">
                              No node UUID
                            </div>
                          )}
                        </div>
                      </td>
                      {/* User Message */}
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="max-w-full">
                          <MessageItem
                            {...convertToChatMessage(message, "user")}
                            isReadOnly={true}
                            loadMedia={false}
                            hideLogo={true}
                          />
                        </div>
                      </td>
                      {/* Endpoint Result */}
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="break-words whitespace-pre-line">
                          {(message.endpoint_result || "N/A").replace(
                            /;/g,
                            "\n"
                          )}

                          {/* Display new endpoint result if available */}
                          {benchmarkResults?.new_message_responses?.[
                            message.query_id
                          ] && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs font-medium text-[#E38C76] mb-2">
                                New Endpoint Result:
                              </div>
                              <div className="break-words whitespace-pre-line text-[#E38C76]">
                                {benchmarkResults.new_message_responses[
                                  message.query_id
                                ].endpoint_result.replace(/;/g, "\n")}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Bot Reply */}
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="max-w-full">
                          <div className="text-xs text-gray-500 mb-2">
                            UUID: {message.bot_message_reply_uuid || "N/A"}
                          </div>
                          <MessageItem
                            {...convertToChatMessage(message, "bot")}
                            isReadOnly={true}
                            loadMedia={false}
                            hideLogo={true}
                          />

                          {/* Display new benchmarked response if available */}
                          {benchmarkResults?.new_message_responses?.[
                            message.query_id
                          ] &&
                            (() => {
                              const newResponse =
                                benchmarkResults.new_message_responses[
                                  message.query_id
                                ];
                              const chatMessage =
                                convertBenchmarkCompareResponseToChatMessage(
                                  newResponse,
                                  message.query_id
                                );

                              if (chatMessage) {
                                return (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs font-medium text-orange-600 mb-2">
                                      New Benchmarked Response:
                                    </div>
                                    <MessageItem
                                      {...chatMessage}
                                      isReadOnly={true}
                                      loadMedia={false}
                                      hideLogo={true}
                                    />
                                  </div>
                                );
                              } else if ("action" in newResponse) {
                                return (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs font-medium text-orange-600 mb-2">
                                      New Benchmarked Action:
                                    </div>
                                    <div className="text-orange-600">
                                      Action: {newResponse.action}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                        </div>
                      </td>
                      {/* Expected Response */}
                      <td className="px-3 py-4">
                        <div className="space-y-2">
                          <div className="text-xs text-gray-500">
                            Selected: {message.expected_response || "None"}
                          </div>
                          <select
                            value={message.expected_response || ""}
                            onChange={(e) =>
                              handleExpectedResponseChange(
                                message.query_id,
                                e.target.value
                              )
                            }
                            disabled={loadingRootNodes}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          >
                            <option value="">Select correct response...</option>

                            {/* Root Nodes */}
                            {rootNodes.length > 0 && (
                              <optgroup label="Root Nodes">
                                {rootNodes.map((node) => (
                                  <option
                                    key={`${message.query_id}-root-${node.uuid}`}
                                    value={node.uuid}
                                  >
                                    {node.content}
                                  </option>
                                ))}
                              </optgroup>
                            )}

                            {/* Endpoint Result Options. TODO: replace with compare_request options */}
                            {message.compare_request &&
                              (() => {
                                const comp_req =
                                  typeof message.compare_request === "string"
                                    ? JSON.parse(message.compare_request)
                                    : message.compare_request;
                                const options = comp_req.options as Option[];
                                const links = comp_req.links as Option[];
                                const actions = comp_req.actions as [];
                                return (
                                  <>
                                    {options.length > 0 && (
                                      <optgroup label="Options from last message">
                                        {options.map((option, index) => {
                                          const [optionText, optionUuid] =
                                            option;
                                          return (
                                            <option
                                              key={`option-${message.query_id}-${optionUuid}-${index}`}
                                              value={optionUuid}
                                            >
                                              {optionText}
                                            </option>
                                          );
                                        })}
                                      </optgroup>
                                    )}
                                    {actions.length > 0 && (
                                      <optgroup label="Endpoint Result Actions">
                                        {actions.map((action, index) => (
                                          <option
                                            key={`${message.query_id}-action-${action}-${index}`}
                                            value={action}
                                          >
                                            {action}
                                          </option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {links.length > 0 && (
                                      <optgroup label="Endpoint Result Links">
                                        {links.map((link, index) => (
                                          <option
                                            key={`${message.query_id}-link-${link}-${index}`}
                                            value={link}
                                          >
                                            {link}
                                          </option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </>
                                );
                              })()}

                            {/* New Benchmarked Response Options */}
                            {benchmarkResults?.new_message_responses?.[
                              message.query_id
                            ] &&
                              (() => {
                                const newResponse =
                                  benchmarkResults.new_message_responses[
                                    message.query_id
                                  ];
                                if (
                                  "options" in newResponse &&
                                  newResponse.options &&
                                  newResponse.options.length > 0
                                ) {
                                  return (
                                    <optgroup label="New Benchmarked Response Options">
                                      {newResponse.options.map(
                                        (
                                          option: [string, string],
                                          index: number
                                        ) => {
                                          const [optionText, optionUuid] =
                                            option;
                                          return (
                                            <option
                                              key={`new-option-${message.query_id}-${optionUuid}-${index}`}
                                              value={optionUuid}
                                              className="text-orange-600"
                                            >
                                              {optionText} (New)
                                            </option>
                                          );
                                        }
                                      )}
                                    </optgroup>
                                  );
                                }
                                return null;
                              })()}

                            {/* New Benchmarked Response Node */}
                            {benchmarkResults?.new_message_responses?.[
                              message.query_id
                            ] &&
                              (() => {
                                const newResponse =
                                  benchmarkResults.new_message_responses[
                                    message.query_id
                                  ];
                                if (
                                  "new_reply_node_uuid" in newResponse &&
                                  "question" in newResponse
                                ) {
                                  return (
                                    <optgroup label="New Benchmarked Response Node">
                                      <option
                                        key={`new-node-${message.query_id}-${newResponse.new_reply_node_uuid}`}
                                        value={newResponse.new_reply_node_uuid}
                                        className="text-orange-600"
                                      >
                                        {newResponse.question} (New)
                                      </option>
                                    </optgroup>
                                  );
                                }
                                return null;
                              })()}
                          </select>
                        </div>
                      </td>
                      {/* Pruned Expected Response */}
                      <td className="px-3 py-4">
                        <div className="space-y-2">
                          <div className="text-xs text-gray-500">
                            Selected:{" "}
                            {message.pruned_expected_response ? (
                              <span className="font-medium">
                                {getNodeDisplayText(
                                  message.pruned_expected_response,
                                  message.expected_response || ""
                                )}
                                {!treeNodesMap.has(
                                  message.expected_response || ""
                                ) && (
                                  <span className="text-gray-500 ml-1">
                                    ({message.pruned_expected_response})
                                  </span>
                                )}
                              </span>
                            ) : (
                              "None"
                            )}
                          </div>
                          <select
                            value={message.pruned_expected_response || ""}
                            onChange={(e) =>
                              handlePrunedExpectedResponseChange(
                                message.query_id,
                                e.target.value
                              )
                            }
                            disabled={loadingRootNodes || loadingTreeNodes}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          >
                            <option value="">
                              {message.pruned_expected_response
                                ? String(message.pruned_expected_response)
                                : "Select pruned response..."}
                            </option>

                            {/* Show nodes from the selected tree */}
                            {message.expected_response ? (
                              treeNodesMap.has(message.expected_response) ? (
                                (treeNodesMap.get(message.expected_response)
                                  ?.length ?? 0) > 0 ? (
                                  <optgroup
                                    label={`Nodes in ${message.expected_response}`}
                                  >
                                    {treeNodesMap
                                      .get(message.expected_response)
                                      ?.map((node, index) => (
                                        <option
                                          key={`$${message.query_id}-tree-node-${index}`}
                                          value={node.uuid}
                                        >
                                          {node.content}
                                        </option>
                                      ))}
                                  </optgroup>
                                ) : (
                                  <option value="" disabled>
                                    No nodes found
                                  </option>
                                )
                              ) : loadingTreeNodes ? (
                                <option value="" disabled>
                                  Loading...
                                </option>
                              ) : (
                                <option
                                  value=""
                                  disabled
                                  className="text-blue-600"
                                >
                                  Tree nodes not loaded
                                </option>
                              )
                            ) : (
                              <option value="" disabled>
                                Select a tree first
                              </option>
                            )}
                          </select>
                          {message.expected_response &&
                            !treeNodesMap.has(message.expected_response) && (
                              <button
                                onClick={() => {
                                  if (message.expected_response) {
                                    handleExpectedResponseChange(
                                      message.query_id,
                                      message.expected_response
                                    );
                                  }
                                }}
                                className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                              >
                                Load Tree Nodes
                              </button>
                            )}
                        </div>
                      </td>
                      {/* Distance */}
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="max-w-full">
                          {(() => {
                            // Check if we have benchmark results for this message
                            const newResponse =
                              benchmarkResults?.new_message_responses?.[
                                message.query_id
                              ];
                            if (
                              newResponse &&
                              "distance" in newResponse &&
                              newResponse.distance !== undefined
                            ) {
                              return (
                                <span className="font-medium text-blue-600">
                                  {newResponse.distance}
                                </span>
                              );
                            }
                            return message.distance || "N/A";
                          })()}
                        </div>
                      </td>
                      {/* Needs a New Tree */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={message.needs_a_new_tree === "true"}
                          onChange={(e) =>
                            handleNeedsNewTreeChange(
                              message.query_id,
                              e.target.checked ? "true" : "false"
                            )
                          }
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </td>
                      {/* Flagged */}
                      <td className="px-3 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={message.is_flagged === "true"}
                          onChange={(e) =>
                            handleIsFlaggedChange(
                              message.query_id,
                              e.target.checked ? "true" : "false"
                            )
                          }
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </td>
                      {/* User Feedback */}
                      <td className="px-3 py-4">
                        <textarea
                          value={getFeedbackValue(
                            message.query_id,
                            message.user_feedback
                          )}
                          onChange={(e) =>
                            handleUserFeedbackChange(
                              message.query_id,
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            handleUserFeedbackBlur(message.query_id)
                          }
                          placeholder="Add feedback..."
                          rows={3}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm resize-none"
                        />
                      </td>
                      {/* Show Details */}
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => toggleRowExpansion(message.query_id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                            title={
                              expandedRows.has(message.query_id)
                                ? "Hide details"
                                : "Show details"
                            }
                          >
                            <svg
                              className={`w-5 h-5 transition-transform ${
                                expandedRows.has(message.query_id)
                                  ? "rotate-180"
                                  : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleReloadRow(message)}
                            disabled={reloadingRows.has(message.query_id)}
                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Reload row data and run new benchmark"
                          >
                            {reloadingRows.has(message.query_id) ? (
                              <svg
                                className="w-5 h-5 animate-spin"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(message.query_id) && (
                      <tr>
                        <td colSpan={11} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <strong>Query ID:</strong> {message.query_id}
                            </div>
                            <div>
                              <strong>Session ID:</strong> {message.session_id}
                            </div>
                            <div>
                              <strong>User Email:</strong> {message.user_email}
                            </div>
                            <div>
                              <strong>Timestamp:</strong>{" "}
                              {formatDateTime(
                                message.user_message_sent_datetime
                              )}
                            </div>
                            <div>
                              <strong>Bot Reply Datetime:</strong>{" "}
                              {formatDateTime(
                                message.bot_message_reply_datetime
                              )}
                            </div>
                            <div>
                              <strong>User Replying to Node UUID:</strong>{" "}
                              {message.user_replying_to_node_uuid || "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply UUID:</strong>{" "}
                              {message.bot_message_reply_uuid || "N/A"}
                            </div>

                            <div>
                              <strong>Found Correct Node:</strong>{" "}
                              {message.found_correct_node || "N/A"}
                            </div>
                            <div>
                              <strong> Expected Response:</strong>{" "}
                              {message.expected_response || "N/A"}
                            </div>
                            <div>
                              <strong> Pruned Expected Response:</strong>{" "}
                              {message.pruned_expected_response ? (
                                <span>
                                  {getNodeDisplayText(
                                    message.pruned_expected_response,
                                    message.expected_response || ""
                                  )}{" "}
                                  <span className="text-xs text-gray-500">
                                    ({message.pruned_expected_response})
                                  </span>
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </div>
                            <div>
                              <strong>Bot Reply Action:</strong>{" "}
                              {message.bot_message_reply_action || "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply Text:</strong>{" "}
                              {message.bot_message_reply_text || "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply Options:</strong>{" "}
                              {message.bot_message_reply_options
                                ? JSON.stringify(
                                    message.bot_message_reply_options
                                  )
                                : "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply Origin:</strong>{" "}
                              {message.bot_message_reply_origin || "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply Audio:</strong>{" "}
                              {message.bot_message_reply_audio || "N/A"}
                            </div>
                            <div>
                              <strong>Elapsed Time to Fetch Audio:</strong>{" "}
                              {message.elapsed_time_to_fetch_audio || "N/A"}
                            </div>
                            <div>
                              <strong>Bot Reply Media:</strong>{" "}
                              {message.bot_message_reply_media || "N/A"}
                            </div>
                            <div>
                              <strong> Elapsed time to fetch media:</strong>{" "}
                              {message.elapsed_time_to_fetch_media || "N/A"}
                            </div>
                            <div>
                              <strong>Endpoint:</strong>{" "}
                              {message.endpoint || "N/A"}
                            </div>
                            <div>
                              <strong> Endpoint result:</strong>{" "}
                              {message.endpoint_result || "N/A"}
                            </div>
                            <div>
                              <strong>Is Flagged:</strong>{" "}
                              {message.is_flagged || "N/A"}
                            </div>
                            <div>
                              <strong>User Feedback:</strong>{" "}
                              {message.user_feedback || "N/A"}
                            </div>
                            <div>
                              <strong>Has Been Reviewed:</strong>{" "}
                              {message.has_been_reviewed || "N/A"}
                            </div>
                            <div>
                              <strong>Needs a New Tree:</strong>{" "}
                              {message.needs_a_new_tree || "not set"}
                            </div>
                            <div>
                              <strong> Prev Expected Responses:</strong>{" "}
                              {message.prev_expected_responses
                                ? JSON.stringify(
                                    message.prev_expected_responses
                                  )
                                : "N/A"}
                            </div>
                            <div>
                              <strong> Benchmark Results:</strong>{" "}
                              {message.benchmark_compare_results
                                ? JSON.stringify(
                                    JSON.stringify(
                                      message.benchmark_compare_results
                                    ).slice(0, 200) + "..."
                                  )
                                : "N/A"}
                            </div>
                            <div>
                              <strong> Compare Request:</strong>{" "}
                              {message.compare_request
                                ? JSON.stringify(message.compare_request)
                                : "N/A"}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareMessagesTable;
